import { ChannelManager, type OpenedChannel } from './protocol/channel-manager.js';
import type { SshTransportProtocol } from './protocol/transport-protocol.js';
import type { DuplexStream, ChannelOptions, SubsystemOptions, TtyConfiguration } from './types.js';
import { Disposable, type Logger, noopLogger } from '@ts-ssh2/common';

/**
 * Streams for an SSH channel (exec or shell).
 */
export type ChannelStream = {
	/** Writable stream to send data to the remote process. */
	stdin: WritableStream<Uint8Array>;
	/** Readable stream of the remote process stdout. */
	stdout: ReadableStream<Uint8Array>;
	/** Readable stream of the remote process stderr. */
	stderr: ReadableStream<Uint8Array>;
	/** Resolves when the remote process exits, with the exit status. */
	exitStatus: Promise<number>;
	/** Send a window-change request (RFC 4254 §6.7) to resize the remote PTY. */
	resize: (cols: number, rows: number) => Promise<void>;
	/** Close the channel gracefully (sends EOF + CLOSE). */
	close: () => Promise<void>;
};

/**
 * Streams for an SSH subsystem channel (e.g. sftp).
 */
export type SubsystemStream = {
	/** Writable stream to send data to the subsystem. */
	stdin: WritableStream<Uint8Array>;
	/** Readable stream of the subsystem output. */
	stdout: ReadableStream<Uint8Array>;
	/** Readable stream of the subsystem stderr. */
	stderr: ReadableStream<Uint8Array>;
	/** Resolves when the subsystem exits, with the exit status. */
	exitStatus: Promise<number>;
	/** Close the channel gracefully (sends EOF + CLOSE). */
	close: () => Promise<void>;
};

/**
 * An authenticated SSH connection.
 *
 * Provides exec(), shell(), subsystem(), and forwardOut() methods that open
 * channels and return streams for communicating with the remote side.
 */
export class Connection extends Disposable {
	private channelManager: ChannelManager;
	private logger: Logger;

	constructor(transport: SshTransportProtocol, logger?: Logger) {
		super();
		this.logger = logger ?? noopLogger;
		this.channelManager = new ChannelManager(transport, this.logger);
	}

	/**
	 * Execute a command on the remote server.
	 *
	 * @param command The command to execute.
	 * @param options Optional PTY, env, and abort signal configuration.
	 * @returns Channel streams with stdin, stdout, stderr, and exit status promise.
	 */
	async exec(command: string, options: ChannelOptions = {}): Promise<ChannelStream> {
		this.logger.debug(`exec: "${command}"`);
		const channel = await this.openConfiguredSession(options);

		// request PTY if configured
		if (options.tty !== false && options.tty) {
			await this.requestPty(channel.localId, options.tty);
		}

		// send exec request
		const accepted = await this.channelManager.sendRequest(channel.localId, 'exec', true, (w) => {
			w.writeString(command);
		});

		if (!accepted) {
			await this.channelManager.closeChannel(channel.localId);
			throw new Error(`Server rejected exec request for: ${command}`);
		}

		return this.buildChannelStream(channel);
	}

	/**
	 * Open an interactive shell on the remote server.
	 *
	 * @param options Optional PTY, env, and abort signal configuration.
	 * @returns Channel streams with stdin, stdout, stderr, and exit status promise.
	 */
	async shell(options: ChannelOptions = {}): Promise<ChannelStream> {
		this.logger.debug('opening shell');
		const channel = await this.openConfiguredSession(options);

		// request PTY (default for shell)
		const ttyConfig: TtyConfiguration =
			options.tty === false ? { term: 'vt100' } : (options.tty ?? {});
		await this.requestPty(channel.localId, ttyConfig);

		// send shell request
		const accepted = await this.channelManager.sendRequest(channel.localId, 'shell', true);

		if (!accepted) {
			await this.channelManager.closeChannel(channel.localId);
			throw new Error('Server rejected shell request');
		}

		return this.buildChannelStream(channel);
	}

	/**
	 * Open a subsystem on the remote server (RFC 4254 §6.5).
	 *
	 * Subsystems are predefined services on the SSH server, such as "sftp".
	 * Unlike exec/shell, subsystems communicate through a binary protocol
	 * on stdin/stdout rather than a terminal.
	 *
	 * @param name The subsystem name (e.g. "sftp").
	 * @param options Optional env and abort signal configuration.
	 * @returns Subsystem streams with stdin, stdout, stderr, and exit status promise.
	 */
	async subsystem(name: string, options: SubsystemOptions = {}): Promise<SubsystemStream> {
		this.logger.debug(`subsystem: "${name}"`);
		const channel = await this.openConfiguredSession(options);

		// send subsystem request
		const accepted = await this.channelManager.sendRequest(
			channel.localId,
			'subsystem',
			true,
			(w) => w.writeString(name),
		);

		if (!accepted) {
			await this.channelManager.closeChannel(channel.localId);
			throw new Error(`Server rejected subsystem request for: ${name}`);
		}

		const exitStatus = this.buildExitStatusPromise(channel.localId);

		return {
			stdin: channel.stdin,
			stdout: channel.stdout,
			stderr: channel.stderr,
			exitStatus,
			close: () => this.channelManager.closeChannel(channel.localId),
		};
	}

	/**
	 * Open a TCP forwarding channel to a remote host:port.
	 *
	 * This opens a "direct-tcpip" channel that tunnels a TCP connection
	 * through the SSH connection. Useful for connection hopping — the
	 * returned DuplexStream can be passed as a pre-existing stream to
	 * a second Client.connect() call.
	 *
	 * @param destHost The destination hostname the SSH server should connect to.
	 * @param destPort The destination port.
	 * @param signal Optional abort signal.
	 * @returns A DuplexStream for the tunneled TCP connection.
	 */
	async forwardOut(
		destHost: string,
		destPort: number,
		signal?: AbortSignal,
	): Promise<DuplexStream> {
		this.logger.debug(`forwardOut: ${destHost}:${destPort}`);
		const channel = await this.channelManager.openDirectTcpip(destHost, destPort, signal);
		return {
			readable: channel.stdout,
			writable: channel.stdin,
		};
	}

	/**
	 * Send a keepalive@openssh.com global request.
	 * Resolves when the server replies (either success or failure counts as alive).
	 */
	async sendKeepaliveRequest(): Promise<void> {
		return this.channelManager.sendKeepaliveRequest();
	}

	// ── private helpers ─────────────────────────────────────────────

	/**
	 * Open a session channel and apply common configuration (env vars).
	 */
	private async openConfiguredSession(options: {
		env?: Record<string, string>;
		signal?: AbortSignal;
	}): Promise<OpenedChannel> {
		const channel = await this.channelManager.openSession(options.signal);

		if (options.env) {
			for (const [name, value] of Object.entries(options.env)) {
				await this.channelManager.sendRequest(channel.localId, 'env', false, (w) => {
					w.writeString(name);
					w.writeString(value);
				});
			}
		}

		return channel;
	}

	/**
	 * Create a promise that resolves when the channel's exit status is received.
	 * Resolves with -1 if the channel closes without sending an exit status.
	 */
	private buildExitStatusPromise(localId: number): Promise<number> {
		return new Promise<number>((resolve) => {
			const exitSub = this.channelManager.onExitStatus.subscribe((evt) => {
				if (evt.channelId === localId) {
					exitSub.dispose();
					closeSub.dispose();
					resolve(evt.exitStatus);
				}
			});
			const closeSub = this.channelManager.onChannelClosed.subscribe((evt) => {
				if (evt.channelId === localId) {
					exitSub.dispose();
					closeSub.dispose();
					resolve(-1);
				}
			});
		});
	}

	/**
	 * Build a ChannelStream from an opened channel.
	 */
	private buildChannelStream(channel: OpenedChannel): ChannelStream {
		return {
			stdin: channel.stdin,
			stdout: channel.stdout,
			stderr: channel.stderr,
			exitStatus: this.buildExitStatusPromise(channel.localId),
			resize: (cols, rows) => this.sendWindowChange(channel.localId, cols, rows),
			close: () => this.channelManager.closeChannel(channel.localId),
		};
	}

	/**
	 * Send a window-change request on a channel (RFC 4254 §6.7).
	 * Uses wantReply: false per the RFC.
	 */
	private async sendWindowChange(localId: number, cols: number, rows: number): Promise<void> {
		await this.channelManager.sendRequest(localId, 'window-change', false, (w) => {
			w.writeUint32(cols);
			w.writeUint32(rows);
			w.writeUint32(0); // pixel width
			w.writeUint32(0); // pixel height
		});
	}

	/**
	 * Request a pseudo-TTY on a channel.
	 */
	private async requestPty(localId: number, config: TtyConfiguration): Promise<void> {
		const term = config.term ?? 'vt100';
		const cols = config.cols ?? 80;
		const rows = config.rows ?? 24;
		const width = config.width ?? 640;
		const height = config.height ?? 480;

		await this.channelManager.sendRequest(localId, 'pty-req', true, (w) => {
			w.writeString(term);
			w.writeUint32(cols);
			w.writeUint32(rows);
			w.writeUint32(width);
			w.writeUint32(height);

			// terminal modes: just an empty string with the terminator byte
			const modes = new Uint8Array([0]); // TTY_OP_END
			w.writeString(modes);
		});
	}

	override dispose(): void {
		this.channelManager.dispose();
		super.dispose();
	}
}
